// CallReview — one screen for every portal section (humpback, baleen whales, dolphin whistles, manatees, dolphin clicks
// & bursts, sperm whales, unsorted). The engine offers, the person adjusts (user 6-7 Oct 2026):
//  • draw / extend / edit the F0 line, harmonics and subharmonics: click = a dot, the next click adds a dot and joins it;
//    Enter (or "Finish") ends the line; drag a dot to move it; double-click a dot to remove it; Delete removes the
//    selected dot or line; Ctrl+Z undoes;
//  • boxes (nonlinear parts, click trains …): drag on the picture with the box tool, move / resize in Select;
//  • clicks of a train: the mark tool adds one per click;
//  • "Everything on this call" lists every item (engine or yours) with delete; deleted engine items can be restored;
//  • Dismiss with a reason (faint, unclear, noise …); species can be corrected (the call then moves to that section);
//  • Confirm saves everything (reviewModel.toReviewDoc) through onSubmit.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../portal/portal-theme.css';
import './CallReview.css';
import { LABELS, LINE_KINDS, PART, SECTION, sectionForSpecies } from './sections';
import { BulkDismiss, DecisionBar, Tick, TypeCard, statusChip } from './ReviewParts';
import { fromReviewDoc, addPoint, derived, drawnLines, fitCallToLines, fromEngine, markAdjusted, removeItem, restoreAll, thin, toReviewDoc, uid, callExtent } from './reviewModel';
import { Info } from './info';
import { LibraryCard } from './Library';
import { LIBRARY_SECTIONS } from './libraryModel';
import { drawSpectrogram, loadCallSound, play } from './liveSpec';
import DetailSelect from './DetailSelect';

const fmt = (v) => (v === null || v === undefined || Number.isNaN(v) ? '—' : String(v));
const r2 = (x) => Math.round(x * 100) / 100;
const r3 = (x) => Math.round(x * 1000) / 1000;
const clone = (o) => JSON.parse(JSON.stringify(o));
const TOOL = {
  select: ['Select / move', 'Click an item to select it. Drag boxes, corners and dots. Click on a selected line to add a dot there.'],
  f0: ['Draw F0', 'Click along the fundamental: each click adds a dot joined to the line. Enter or Finish ends the line.'],
  harmonic: ['Draw harmonic', 'Click along a harmonic band. Its number (×2, ×3 …) is set from the F0 line when you finish; you can change it.'],
  subharmonic: ['Draw subharmonic', 'Click along a band below / between the harmonics (F0/2 …). Enter or Finish ends the line.'],
  mark: ['Mark clicks', 'Click on each click (vertical line) to mark it. Select a mark and press Delete to remove it.'],
  box: ['Draw box', 'Drag on the picture to draw a box of the kind chosen under "Mark a part" (frequency jump, subharmonics, biphonation, chaos, sidebands, possible aliasing, other sound, missed call …).'],
  zoom: ['Zoom', 'Drag a box around what you want to see larger. Ctrl / ⌘ + mouse wheel zooms at the pointer; "Full range" goes back.'],
  pan: ['Move view', 'Drag the picture to move the zoomed view.'],
};
const ZOOM_TOOLS = ['zoom', 'pan'];
const ORIGIN = { engine: 'engine', adjusted: 'adjusted by you', added: 'added by you' };


function lineName(l, i) {
  if (l.kind === 'f0') return `F0 line${i ? ` ${i + 1}` : ''}`;
  if (l.kind === 'harmonic') return `Harmonic ×${l.k}`;
  return `Subharmonic ${l.k && l.k < 1 ? `F0/${Math.round(1 / l.k)}` : ''}`.trim();
}
const span = (pts) => (pts.length ? `${pts[0][0].toFixed(2)}–${pts[pts.length - 1][0].toFixed(2)} s` : '');

export default function CallReview({ sectionKey, calls, sourceLabel, meta = {}, onSubmit, header, empty = 'No calls to review here yet.',
  confirmText = 'Confirm & save', finalStatus = 'confirmed', note, level = 'researcher', library = null }) {
  // decoder (portal plans): mark the call (box, other sounds, non-linear phenomena) and its type with the cheat sheet;
  // F0 / harmonic lines, the parts bar and the measurements are the Researcher's tools. The engine's lines stay in the
  // saved review unchanged.
  const full = level !== 'decoder';
  const section = useMemo(() => (full ? SECTION[sectionKey]
    : { ...SECTION[sectionKey], tools: ['select', 'box'], partsBar: false }), [full, sectionKey]);
  const [sel, setSel] = useState(0);
  const [work, setWork] = useState({});
  const [status, setStatus] = useState({});            // per call: saving | confirmed | dismissed | moved:<section> | error
  const [tool, setTool] = useState('select');
  const [boxKind, setBoxKind] = useState(Object.keys(section.boxes)[0]);
  const [pick, setPick] = useState(null);               // {g, id, pi}
  const [drawing, setDrawing] = useState(null);         // id of the line being drawn
  const [rubber, setRubber] = useState(null);
  const [zoomBox, setZoomBox] = useState(null);         // rubber band of the Zoom tool
  const [view, setView] = useState(null);               // {t0, t1, f0, f1} shown; null = the call's start view
  const [detail, setDetail] = useState(0);              // window: 0 auto, < 0 sharper in time, > 0 sharper in frequency
  const [winInfo, setWinInfo] = useState(null);
  const [sig, setSig] = useState(null);                 // the call's sound (live spectrogram + playback), engine 0.4.12
  const [sigMsg, setSigMsg] = useState('');
  const [playing, setPlaying] = useState(null);         // {handle, what}
  const [playPos, setPlayPos] = useState(null);
  const [speed, setSpeed] = useState(1);
  const [loop, setLoop] = useState(false);
  const [bandOnly, setBandOnly] = useState(false);
  const canvasRef = useRef(null);
  const spaceRef = useRef(null);
  const [show, setShow] = useState({ call: true, lines: full, boxes: true, parts: full, marks: true });
  const [error, setError] = useState('');
  const [ticked, setTicked] = useState(new Set());       // items ticked for "Dismiss several"
  const plot = useRef(null);
  const hist = useRef({});

  const c = calls[sel];
  useEffect(() => { setSel(0); setWork({}); setStatus({}); hist.current = {}; setTicked(new Set()); }, [calls]);
  useEffect(() => { setPick(null); setDrawing(null); setError(''); setView(null); setDetail(0); }, [sel, calls]);
  const cur = calls[sel];
  useEffect(() => {                    // the call's sound: longer clip (0.4.12) or the frame sound of older analyses
    setSig(null); setSigMsg('');
    if (!cur) return undefined;
    let alive = true;
    loadCallSound(cur).then((x) => { if (alive) setSig(x); }).catch((e) => { if (alive) setSigMsg(String(e.message || e)); });
    // slow baleen calls are played faster (below ~100 Hz you hear nothing at normal speed)
    const top = (cur.call && cur.call.f1) || cur.viewFmax || cur.fmax || 0;
    setSpeed(top && top < 120 ? 10 : (top && top < 600 ? 4 : 1));
    return () => { alive = false; };
  }, [cur]);
  useEffect(() => {
    if (c && !work[c.id]) setWork((p) => (p[c.id] ? p : { ...p, [c.id]: c._prev ? fromReviewDoc(c, section, c._prev) : fromEngine(c, section) }));
  }, [c, work, section]);
  const w = c ? work[c.id] : null;
  const locked = !!(c && status[c.id] && status[c.id] !== 'error');     // saved calls are done (not edited again)

  const change = useCallback((fn, snap = true) => {
    if (locked) return;
    setWork((prev) => {
      if (!prev[c.id]) return prev;
      if (snap) { const h = hist.current[c.id] || []; h.push(clone(prev[c.id])); if (h.length > 60) h.shift(); hist.current[c.id] = h; }
      const next = clone(prev[c.id]);
      fn(next);
      return { ...prev, [c.id]: next };
    });
  }, [c, locked]);
  const undo = useCallback(() => {
    const h = hist.current[c.id] || [];
    if (!h.length) return;
    const prev = h.pop();
    setWork((p) => ({ ...p, [c.id]: prev })); setPick(null); setDrawing(null);
  }, [c]);

  const fmax = c ? c.fmax || 4000 : 4000;
  // zoomable view (13 Oct 2026): the picture reaches the recording's limit (fmax = half the sample rate); baleen /
  // humpback calls far below it open zoomed to their band (viewFmax). Everything drawn is placed in view coordinates.
  // with the sound loaded the picture is drawn live: zoom out to the whole sound clip and up to half its sample rate
  const live = !!(c && sig);
  const ext = live ? { t0: Math.min(sig.t0, c.x0), t1: Math.max(sig.t1, c.x1), f1: Math.max(fmax, sig.fs / 2) }
    : { t0: c ? c.x0 : 0, t1: c ? c.x1 : 1, f1: fmax };
  const startView = c ? { t0: c.x0, t1: c.x1, f0: 0, f1: Math.min(fmax, c.viewFmax || fmax) } : { t0: 0, t1: 1, f0: 0, f1: fmax };
  const v = view || startView;
  const fullView = { t0: ext.t0, t1: ext.t1, f0: 0, f1: ext.f1 };
  const isFull = v.t0 <= fullView.t0 + 1e-6 && v.t1 >= fullView.t1 - 1e-6 && v.f0 <= 0 && v.f1 >= ext.f1 - 1e-6;
  const X = (t) => ((t - v.t0) / (v.t1 - v.t0)) * 100;
  const Y = (f) => (1 - (f - v.f0) / (v.f1 - v.f0)) * 100;
  const at = (e) => {
    const r = plot.current.getBoundingClientRect();
    const px = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    const py = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
    return { t: v.t0 + px * (v.t1 - v.t0), f: v.f0 + (1 - py) * (v.f1 - v.f0), r };
  };
  const clampView = (q) => {
    const minT = (c.x1 - c.x0) / 64; const minF = ext.f1 / 512;
    let { t0, t1, f0, f1 } = q;
    if (t1 - t0 < minT) { const m = (t0 + t1) / 2; t0 = m - minT / 2; t1 = m + minT / 2; }
    if (f1 - f0 < minF) { const m = (f0 + f1) / 2; f0 = m - minF / 2; f1 = m + minF / 2; }
    const wt = Math.min(t1 - t0, ext.t1 - ext.t0); const wf = Math.min(f1 - f0, ext.f1);
    t0 = Math.max(ext.t0, Math.min(t0, ext.t1 - wt)); f0 = Math.max(0, Math.min(f0, ext.f1 - wf));
    return { t0, t1: t0 + wt, f0, f1: f0 + wf };
  };
  // live drawing: redraw (once per frame) when the view or the sound changes
  // window (user 13 Oct 2026, all calls): never shorter than the start / full view's window, so zooming in enlarges
  // the same clear contour seen zoomed out; the Detail menu trades time for frequency detail
  const [plotW, setPlotW] = useState(0);               // redraw when the picture changes size (window / layout)
  useEffect(() => {
    const el = plot.current; if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setPlotW(Math.round(el.clientWidth))); ro.observe(el); return () => ro.disconnect();
  }, [live]);
  const drawKey = live ? `${plotW}|${sig.t0}|${v.t0.toFixed(4)}|${v.t1.toFixed(4)}|${v.f0.toFixed(1)}|${v.f1.toFixed(1)}|${detail}|${ext.t0}|${ext.t1}` : '';
  useEffect(() => {
    if (!live) return undefined;
    const id = requestAnimationFrame(() => {
      try { setWinInfo(drawSpectrogram(canvasRef.current, sig, v, { minView: [startView, fullView], detail })); } catch (e) { setSigMsg(String(e.message || e)); }
    });
    return () => cancelAnimationFrame(id);
  }, [drawKey]);
  // playback
  const stopPlay = useCallback(() => { setPlaying((p) => { if (p) p.handle.stop(); return null; }); setPlayPos(null); }, []);
  const startPlay = (a, b, what) => {
    if (!sig) return;
    if (playing) playing.handle.stop();
    const handle = play(sig, Math.max(sig.t0, a), Math.min(sig.t1, b), { speed, loop, band: bandOnly ? [v.f0, v.f1] : null });
    setPlaying({ handle, what });
  };
  useEffect(() => {
    if (!playing) return undefined;
    let id;
    const tick = () => { const pos = playing.handle.position(); if (pos === null) { setPlaying(null); setPlayPos(null); return; } setPlayPos(pos); id = requestAnimationFrame(tick); };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [playing]);
  useEffect(() => () => { if (playing) playing.handle.stop(); }, [sel]);
  const zoomBy = (k, ct = (v.t0 + v.t1) / 2, cf = (v.f0 + v.f1) / 2) => setView(clampView({
    t0: ct - (ct - v.t0) * k, t1: ct + (v.t1 - ct) * k, f0: cf - (cf - v.f0) * k, f1: cf + (v.f1 - cf) * k }));
  const zoomRef = useRef(null);
  zoomRef.current = { at, zoomBy };
  useEffect(() => {                    // Ctrl / ⌘ + wheel zooms at the pointer (a native, non-passive listener)
    const el = plot.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const z = zoomRef.current; const p = z.at(e);
      z.zoomBy(e.deltaY < 0 ? 0.8 : 1.25, p.t, p.f);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [c]);

  // ---------------------------------------------------------------- lines
  const finishLine = useCallback(() => {
    if (!drawing) return;
    change((s) => {
      const l = s.lines.find((x) => x.id === drawing);
      if (!l) return;
      if (l.points.length < 2) { s.lines = s.lines.filter((x) => x.id !== drawing); return; }
      if (l.origin === 'added' && (l.kind === 'harmonic' || l.kind === 'subharmonic')) {
        const f0 = s.lines.filter((x) => x.kind === 'f0').flatMap((x) => x.points).sort((a, b) => a[0] - b[0]);
        if (f0.length) {
          const near = (t) => f0.reduce((best, p) => (Math.abs(p[0] - t) < Math.abs(best[0] - t) ? p : best), f0[0])[1];
          const ratios = l.points.map((p) => p[1] / near(p[0])).sort((a, b) => a - b);
          const q = ratios[Math.floor(ratios.length / 2)];
          if (l.kind === 'harmonic') l.k = Math.max(2, Math.round(q));
          else l.k = 1 / Math.max(2, Math.round(1 / Math.max(0.05, q)));
        } else if (l.kind === 'subharmonic') l.k = 0.5;
      }
    }, false);
    setDrawing(null);
  }, [drawing, change]);

  const startLine = (kind, t, f) => {
    const id = uid('l');
    change((s) => { s.lines.push({ id, kind, k: kind === 'harmonic' ? 2 : (kind === 'subharmonic' ? 0.5 : 1), points: [[r3(t), Math.round(f * 10) / 10]], origin: 'added' }); });
    setDrawing(id); setPick({ g: 'lines', id, pi: 0 });
  };
  const extendLine = (id) => {
    const l = w.lines.find((x) => x.id === id);
    if (!l) return;
    if (l.follows) materialise(id);
    setTool(l.kind); setDrawing(id); setPick({ g: 'lines', id });
  };
  const materialise = (id) => change((s) => {
    const l = s.lines.find((x) => x.id === id);
    if (!l || !l.follows) return;
    const pts = s.lines.filter((x) => x.kind === 'f0').flatMap((x) => x.points).sort((a, b) => a[0] - b[0]).map(([t, f]) => [t, Math.round(f * l.k * 10) / 10]);
    l.points = thin(pts); delete l.follows; markAdjusted(l);
  });

  // ---------------------------------------------------------------- dragging (boxes, call box, dots)
  const drag = (e, onMove) => {
    e.stopPropagation(); e.preventDefault();
    const start = at(e);
    let first = true;
    const move = (ev) => { const p = at(ev); onMove(p.t - start.t, p.f - start.f, p, first); first = false; };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const getBox = (s, id) => (id === 'call' ? s.call : s.boxes.find((b) => b.id === id));
  const startBoxDrag = (e, id, mode) => {
    if (tool !== 'select') return;
    setPick({ g: id === 'call' ? 'call' : 'boxes', id });
    const o = { ...getBox(w, id) };
    drag(e, (dt, df, p, first) => change((s) => {
      const b = getBox(s, id);
      if (mode === 'move') {
        b.t0 = r2(o.t0 + dt); b.t1 = r2(o.t1 + dt);
        const sh = Math.max(-o.f0, Math.min(fmax - o.f1, df)); b.f0 = Math.round(o.f0 + sh); b.f1 = Math.round(o.f1 + sh);
      } else {
        if (mode.includes('w')) b.t0 = r2(Math.min(o.t1 - 0.02, o.t0 + dt));
        if (mode.includes('e')) b.t1 = r2(Math.max(o.t0 + 0.02, o.t1 + dt));
        if (mode.includes('n')) b.f1 = Math.round(Math.min(fmax, Math.max(o.f0 + 10, o.f1 + df)));
        if (mode.includes('s')) b.f0 = Math.round(Math.max(0, Math.min(o.f1 - 10, o.f0 + df)));
      }
      markAdjusted(b);
    }, first));
  };
  const startDotDrag = (e, id, pi) => {
    if (tool !== 'select' && drawing !== id) return;
    setPick({ g: 'lines', id, pi });
    const o = w.lines.find((x) => x.id === id).points[pi];
    drag(e, (dt, df, p, first) => change((s) => {
      const l = s.lines.find((x) => x.id === id); if (!l) return;
      l.points[pi] = [r3(o[0] + dt), Math.round(Math.max(0, Math.min(fmax, o[1] + df)) * 10) / 10];
      l.points.sort((a, b) => a[0] - b[0]); markAdjusted(l);
    }, first));
  };
  const removeDot = useCallback((id, pi) => change((s) => {
    const l = s.lines.find((x) => x.id === id); if (!l) return;
    if (l.points.length <= 2) removeItem(s, 'lines', id); else { l.points.splice(pi, 1); markAdjusted(l); }
  }), [change]);

  // ---------------------------------------------------------------- clicks on the picture
  const onPlotDown = (e) => {
    if (!c || e.button > 0) return;
    const p = at(e);
    if (tool === 'zoom') {
      e.preventDefault();
      const s0 = p;
      setZoomBox({ t0: s0.t, t1: s0.t, f0: s0.f, f1: s0.f });
      const move = (ev) => { const q = at(ev); setZoomBox({ t0: Math.min(s0.t, q.t), t1: Math.max(s0.t, q.t), f0: Math.min(s0.f, q.f), f1: Math.max(s0.f, q.f) }); };
      const up = (ev) => {
        window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
        const q = at(ev); setZoomBox(null);
        const b = { t0: Math.min(s0.t, q.t), t1: Math.max(s0.t, q.t), f0: Math.min(s0.f, q.f), f1: Math.max(s0.f, q.f) };
        if (X(b.t1) - X(b.t0) < 2 && Y(b.f0) - Y(b.f1) < 2) zoomBy(0.5, s0.t, s0.f);      // a click: zoom in 2x there
        else if (X(b.t1) - X(b.t0) < 2) setView(clampView({ ...v, f0: b.f0, f1: b.f1 }));  // a vertical drag: frequency only
        else if (Y(b.f0) - Y(b.f1) < 2) setView(clampView({ ...v, t0: b.t0, t1: b.t1 }));  // a horizontal drag: time only
        else setView(clampView(b));
      };
      window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
      return;
    }
    if (tool === 'pan') {
      e.preventDefault();
      const r = plot.current.getBoundingClientRect(); const v0 = v; const x0 = e.clientX; const y0 = e.clientY;
      const move = (ev) => {
        const dt = ((ev.clientX - x0) / r.width) * (v0.t1 - v0.t0); const df = ((ev.clientY - y0) / r.height) * (v0.f1 - v0.f0);
        setView(clampView({ t0: v0.t0 - dt, t1: v0.t1 - dt, f0: v0.f0 + df, f1: v0.f1 + df }));
      };
      const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
      window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
      return;
    }
    if (['f0', 'harmonic', 'subharmonic'].includes(tool)) {
      const l = drawing && w.lines.find((x) => x.id === drawing);
      if (l && l.kind === tool) {
        let idx = 0;
        change((s) => { idx = addPoint(s.lines.find((x) => x.id === drawing), p.t, p.f); markAdjusted(s.lines.find((x) => x.id === drawing)); });
        setPick({ g: 'lines', id: drawing, pi: idx });
      } else { if (drawing) finishLine(); startLine(tool, p.t, p.f); }
      return;
    }
    if (tool === 'mark') {
      const id = uid('m');
      change((s) => s.marks.push({ id, t: r3(p.t), f: Math.round(p.f), origin: 'added' }));
      setPick({ g: 'marks', id });
      return;
    }
    if (tool === 'box') {
      e.preventDefault();
      const s0 = p;
      setRubber({ t0: s0.t, t1: s0.t, f0: s0.f, f1: s0.f });
      const move = (ev) => { const q = at(ev); setRubber({ t0: Math.min(s0.t, q.t), t1: Math.max(s0.t, q.t), f0: Math.min(s0.f, q.f), f1: Math.max(s0.f, q.f) }); };
      const up = (ev) => {
        window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
        const q = at(ev); setRubber(null);
        const b = { t0: r2(Math.min(s0.t, q.t)), t1: r2(Math.max(s0.t, q.t)), f0: Math.round(Math.min(s0.f, q.f)), f1: Math.round(Math.max(s0.f, q.f)) };
        if (X(b.t1) - X(b.t0) < 0.8 || Y(b.f0) - Y(b.f1) < 2) return;          // a click, not a drag
        const id = uid('b');
        change((s) => s.boxes.push({ id, kind: boxKind, ...b, origin: 'added' }));
        setPick({ g: 'boxes', id }); setShow((v) => ({ ...v, boxes: true }));
      };
      window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
      return;
    }
    setPick(null);
  };
  const onLineDown = (e, l) => {
    if (tool !== 'select') return;
    e.stopPropagation();
    if (pick && pick.g === 'lines' && pick.id === l.id && !l.follows) {   // second click on a selected line: add a dot there
      const p = at(e); let idx = 0;
      change((s) => { const x = s.lines.find((y) => y.id === l.id); idx = addPoint(x, p.t, p.f); markAdjusted(x); });
      setPick({ g: 'lines', id: l.id, pi: idx });
    } else setPick({ g: 'lines', id: l.id });
  };

  const deleteSelected = useCallback(() => {
    if (!pick || pick.g === 'call') return;
    if (pick.g === 'lines' && pick.pi !== undefined && pick.pi !== null) { removeDot(pick.id, pick.pi); setPick({ g: 'lines', id: pick.id }); return; }
    change((s) => removeItem(s, pick.g, pick.id)); setPick(null);
  }, [pick, change, removeDot]);

  useEffect(() => {
    const key = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (['INPUT', 'SELECT', 'TEXTAREA'].includes(tag)) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); return; }
      if (e.key === ' ' && !(e.target && e.target.tagName === 'BUTTON')) { e.preventDefault(); if (spaceRef.current) spaceRef.current(); return; }
      if (e.key === 'Enter' && drawing) { e.preventDefault(); finishLine(); return; }
      if (e.key === 'Escape') { if (drawing) finishLine(); else { setPick(null); setTool('select'); } return; }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (drawing) {
          e.preventDefault();
          change((s) => { const l = s.lines.find((x) => x.id === drawing); if (l && l.points.length) l.points.splice(pick && pick.pi != null ? pick.pi : l.points.length - 1, 1); });
          setPick({ g: 'lines', id: drawing });
          return;
        }
        if (pick) { e.preventDefault(); deleteSelected(); }
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [drawing, pick, undo, finishLine, deleteSelected, change]);

  spaceRef.current = () => { if (playing) stopPlay(); else startPlay(v.t0, v.t1, 'view'); };
  if (!c) return <div className="hcr">{header}{empty && <div className="ct-card"><p className="ct-muted">{empty}</p></div>}</div>;
  if (!w) return <div className="hcr">{header}</div>;

  // ---------------------------------------------------------------- derived values for display
  const lines = drawnLines(w);
  const drv = derived(section, w);
  const engineSpecies = c.speciesId || null;
  const signal = section.signal === 'pulsed' ? 'pulsed' : 'tonal';
  const moveTo = w.label.speciesId && w.label.speciesId !== engineSpecies ? sectionForSpecies(w.label.speciesId, signal) : null;
  const moving = moveTo && moveTo !== section.key;
  const groups = section.groups || [['Measurements', Object.keys(c.m || {})]];
  const shownVal = (k) => (k in w.edits ? w.edits[k] : (k in drv && String(drv[k]) !== fmt(c.m?.[k]) ? String(drv[k]) : fmt(c.m?.[k])));
  const sb = pick && (pick.g === 'call' ? w.call : (pick.g === 'boxes' ? w.boxes.find((b) => b.id === pick.id) : null));
  const sl = pick && pick.g === 'lines' ? w.lines.find((l) => l.id === pick.id) : null;
  const sp = pick && pick.g === 'parts' ? w.parts.find((p) => p.id === pick.id) : null;
  const sm = pick && pick.g === 'marks' ? w.marks.find((m) => m.id === pick.id) : null;
  const ticks = [0, 1, 2, 3, 4].map((i) => (v.t0 + ((v.t1 - v.t0) * i) / 4).toFixed(v.t1 - v.t0 < 0.4 ? 3 : 2));
  const removedN = Object.values(w.removed).reduce((a, v) => a + v.length, 0);
  const st = status[c.id];
  const kindColor = (k) => (section.boxes[k] || { color: '#adb5bd' }).color;
  const yLabel = (k) => { const f = v.f0 + (k * (v.f1 - v.f0)) / 4; return f ? (f >= 1000 ? `${r2(f / 1000)} k` : `${Math.round(f)}`) : '0 Hz'; };
  const kHz = (f) => (f >= 1000 ? `${r2(f / 1000)} kHz` : `${Math.round(f)} Hz`);

  const submit = async (decision) => {
    if (section.needsSpecies && !w.label.speciesId && decision.status === 'confirmed') { setError('Set the species first — this call is in Unsorted.'); return; }
    if (drawing) finishLine();
    const doc = toReviewDoc(c, w, section, { ...meta, ...(c._meta || {}) }, decision.status === 'confirmed' ? { ...decision, status: finalStatus } : decision);
    setStatus((s) => ({ ...s, [c.id]: 'saving' })); setError('');
    try {
      if (onSubmit) await onSubmit(c, doc);
      const done = doc.species.moveTo ? `moved:${doc.species.moveTo}` : doc.status;
      setStatus((s) => ({ ...s, [c.id]: done }));
      const nxt = calls.findIndex((x, i) => i > sel && !status[x.id]);
      if (nxt >= 0) setSel(nxt);
    } catch (err) {
      setStatus((s) => ({ ...s, [c.id]: 'error' })); setError(`Not saved: ${err.message || err}`);
    }
  };
  const chip = statusChip;
  const row = (g, id, name, detail, origin, color, extra) => {
    const on = pick && pick.g === g && pick.id === id;
    return (
      <li key={`${g}${id}`} className={`cr-el ${on ? 'on' : ''}`}>
        <button type="button" className="cr-el-main" onClick={() => setPick({ g, id })}>
          <span className="cr-dot" style={{ background: color }} />
          <span className="cr-el-name">{name}</span>
          <span className="ct-muted cr-el-detail">{detail}</span>
          {origin && <span className={`cr-origin ${origin}`}>{ORIGIN[origin]}</span>}
        </button>
        {extra}
        {g !== 'call' && <button type="button" className="cr-x" aria-label={`Delete ${name}`} title="Delete"
                                 onClick={() => { change((s) => removeItem(s, g, id)); if (on) setPick(null); }}>×</button>}
      </li>);
  };

  return (
    <div className="hcr cr-wide">
      {header}
      <div className="hcr-body">
        <nav className="hcr-list" aria-label="Calls">
          <div className="ct-muted hcr-small">{sourceLabel}</div>
          {calls.some((x) => x.chorus) && (
            <div className="hcr-note hcr-chorus-note"><strong>Chorus recording.</strong> Several whales call at once in parts of
              this recording. There only the clearest calls are boxed (marked “chorus”), and their limits are not precise yet —
              adjust them, or dismiss calls you cannot tell apart.</div>)}
          <BulkDismiss calls={calls} ticked={ticked} setTicked={setTicked} status={status} setStatus={setStatus} onSubmit={onSubmit}
                       makeDoc={(x, d) => toReviewDoc(x, work[x.id] || (x._prev ? fromReviewDoc(x, section, x._prev) : fromEngine(x, section)), section, { ...meta, ...(x._meta || {}) }, d)} />
          {calls.map((x, i) => {
            const [txt, cls] = chip(status[x.id]);
            return (
              <div key={x.id} className="hcr-itemrow"><Tick id={x.id} ticked={ticked} setTicked={setTicked} disabled={!!status[x.id]} />
              <button type="button" className={`hcr-item ${i === sel ? 'on' : ''}`} onClick={() => setSel(i)}>
                <span className="hcr-item-top"><strong>{x.title || `Call ${i + 1}`}</strong><span className={`hcr-chip ${cls}`}>{txt}</span></span>
                <span className="hcr-mono hcr-file">{(work[x.id]?.label.code) ?? x.code}{x.chorus && <span className="hcr-chorus-chip">chorus</span>}</span>
                <span className="ct-muted">{x.species || x.broad || ''}</span>
              </button></div>);
          })}
        </nav>

        <div className="hcr-main">
          <div className="ct-card">
            <div className="hcr-row-between">
              <h3>{c.title || `Call ${sel + 1}`} <Info id="spectrogram" /></h3>
              <span className="ct-muted">{c.sampleRate ? `${c.sampleRate / 1000} kHz recording · ` : ''}{isFull ? `0–${kHz(ext.f1)} (full range)` : `${kHz(v.f0)}–${kHz(v.f1)} shown of 0–${kHz(ext.f1)}`}</span>
            </div>

            {c._prev && <p className="cr-prev">Reviewed by a decoder: <strong>{c._prev.status === 'dismissed' ? `dismissed (${c._prev.dismiss?.reason || ''}${c._prev.dismiss?.note ? `: ${c._prev.dismiss.note}` : ''})` : 'confirmed'}</strong>
              {c._prev.label?.code ? <> · code <span className="hcr-mono">{c._prev.label.code}</span></> : null} — their lines, boxes and edits are loaded below.</p>}
            <div className="cr-tools" role="toolbar" aria-label="Drawing tools">
              {section.tools.map((t) => (
                <button key={t} type="button" className={`cr-tool ${tool === t ? 'on' : ''}`} aria-pressed={tool === t}
                        onClick={() => { if (drawing) finishLine(); setTool(t); }}>
                  {t !== 'select' && t !== 'box' && t !== 'mark' && <span className="cr-dot" style={{ background: LINE_KINDS[t].color }} />}
                  {TOOL[t][0]}</button>))}
              <span className="cr-tools-right">
                {drawing && <button type="button" className="ct-btn green" onClick={finishLine}>Finish line</button>}
                <button type="button" className="ct-btn" onClick={undo} disabled={!(hist.current[c.id] || []).length}>Undo</button>
              </span>
            </div>
            <div className="cr-zoombar" role="toolbar" aria-label="Zoom">
              <span className="ct-muted hcr-small">View:</span>
              {ZOOM_TOOLS.map((t) => (
                <button key={t} type="button" className={`cr-tool ${tool === t ? 'on' : ''}`} aria-pressed={tool === t}
                        onClick={() => { if (drawing) finishLine(); setTool(tool === t ? 'select' : t); }}>{t === 'zoom' ? '🔍 ' : '✋ '}{TOOL[t][0]}</button>))}
              <button type="button" className="ct-btn" aria-label="Zoom in" onClick={() => zoomBy(0.5)}>＋</button>
              <button type="button" className="ct-btn" aria-label="Zoom out" onClick={() => zoomBy(2)} disabled={isFull}
                      title={isFull && !(sig && sig.full) ? 'This call was analysed before engine 0.4.12: only the picture’s stretch exists. After re-analysis you can zoom out to ± seconds around the call.' : 'Zoom out'}>−</button>
              <button type="button" className="ct-btn" onClick={() => { setView(null); setDetail(0); }} disabled={!view && !detail} title="Back to the view the call opened with (and automatic detail)">↺ Default view</button>
              <button type="button" className="ct-btn" onClick={() => setView(fullView)} disabled={isFull}>Full range</button>
              {live && <DetailSelect value={detail} onChange={setDetail} info={winInfo} />}
            </div>
            <div className="cr-soundbar" role="toolbar" aria-label="Sound">
              <span className="ct-muted hcr-small">Sound:</span>
              {!sig && !sigMsg && <span className="ct-muted hcr-small">loading…</span>}
              {sigMsg && <span className="ct-muted hcr-small">no sound for this call ({sigMsg})</span>}
              {sig && <>
                {playing ? <button type="button" className="ct-btn" onClick={stopPlay}>■ Stop</button>
                  : <button type="button" className="ct-btn green" onClick={() => startPlay(v.t0, v.t1, 'view')} title="Play what is in view (Space)">▶ Play view</button>}
                <button type="button" className="ct-btn" onClick={() => { const b = (pick && pick.g === 'boxes' && w.boxes.find((x) => x.id === pick.id)) || w.call; startPlay(b.t0, b.t1, 'box'); }}
                        title="Play the whole-call box, or the selected box">▶ Play {pick && pick.g === 'boxes' ? 'box' : 'call'}</button>
                <label className="hcr-small">Speed <select className="hcr-input cr-speed" value={speed} onChange={(e) => setSpeed(Number(e.target.value))}>
                  {[0.25, 0.5, 1, 2, 4, 10].map((k) => <option key={k} value={k}>{k === 1 ? 'normal' : `${k}×`}</option>)}</select></label>
                <label className="hcr-small ct-check"><input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} /> loop</label>
                <label className="hcr-small ct-check" title="Play only the frequencies shown (cuts noise outside the view)"><input type="checkbox" checked={bandOnly} onChange={(e) => setBandOnly(e.target.checked)} /> only the band in view</label>
                {!sig.full && <span className="cr-warn hcr-small">Analysed before engine 0.4.12: no sound around the call, so you can only zoom out to the picture’s stretch ({(c.x1 - c.x0).toFixed(1)} s). It zooms out to ± seconds once this recording is analysed again.</span>}
              </>}
            </div>
            {section.tools.includes('box') && (
              // every box kind of this section as its own button (user, 9 Oct 2026: the non-linear types were hidden
              // behind "Draw box"): one click = draw that kind
              <div className="cr-kinds" role="toolbar" aria-label="Mark a part of the call">
                <span className="ct-muted hcr-small">Mark a part:</span>
                {Object.entries(section.boxes).map(([k, v]) => (
                  <button key={k} type="button" className={`cr-kind ${tool === 'box' && boxKind === k ? 'on' : ''}`}
                          aria-pressed={tool === 'box' && boxKind === k}
                          onClick={() => { if (drawing) finishLine(); setBoxKind(k); setTool('box'); }}>
                    <span className="cr-dot" style={{ background: v.color }} />{v.name}</button>))}
              </div>)}
            <p className="ct-muted cr-hint">{drawing ? 'Drawing: click to add dots (before the start or after the end extends the line). Backspace removes the last dot, Enter finishes.' : TOOL[tool][1]}</p>

            <div className="hcr-plotwrap">
              <div className="hcr-yaxis hcr-mono">{[4, 3, 2, 1, 0].map((k) => <span key={k}>{yLabel(k)}</span>)}</div>
              <div className={`hcr-plot cr-tool-${tool}`} ref={plot} onPointerDown={onPlotDown}>
                {live
                  ? <canvas ref={canvasRef} className="cr-live" aria-label={`Spectrogram of ${c.title || 'the call'}`} />
                  : <img src={c.img} alt={`Spectrogram of ${c.title || 'the call'}`} draggable={false}
                         style={{ left: `${X(c.x0)}%`, top: `${Y(fmax)}%`, width: `${X(c.x1) - X(c.x0)}%`, height: `${Y(0) - Y(fmax)}%`, right: 'auto', bottom: 'auto' }} />}
                {playPos !== null && playPos >= v.t0 && playPos <= v.t1 && <div className="cr-playhead" style={{ left: `${X(playPos)}%` }} />}
                {show.lines && (
                  <svg viewBox="0 0 1000 1000" preserveAspectRatio="none" className={tool === 'select' ? 'cr-svg-live' : ''}>
                    {lines.map((l) => {
                      const pts = l.points.filter(([, f]) => f <= fmax).map(([t, f]) => `${X(t) * 10},${Y(f) * 10}`).join(' ');
                      if (!pts) return null;
                      const on = pick && pick.g === 'lines' && pick.id === l.id;
                      const k = LINE_KINDS[l.kind];
                      return (
                        <g key={`${l.id}-${l.seg}`} onPointerDown={(e) => onLineDown(e, l)}>
                          <polyline points={pts} className="cr-hit" vectorEffect="non-scaling-stroke" />
                          <polyline points={pts} fill="none" stroke={k.color} strokeWidth={on ? k.width + 1.6 : k.width}
                                    strokeOpacity={l.follows ? 0.55 : 0.95} strokeDasharray={l.follows ? '6 4' : undefined}
                                    vectorEffect="non-scaling-stroke" />
                        </g>);
                    })}
                  </svg>)}
                {show.lines && lines.filter((l) => !l.follows && ((pick && pick.g === 'lines' && pick.id === l.id) || drawing === l.id)).map((l) => l.points.map(([t, f], pi) => (
                  <span key={`${l.id}d${pi}`} role="button" tabIndex={-1} aria-label={`Dot ${pi + 1} at ${t} s, ${f} Hz`}
                        className={`cr-pt ${pick && pick.pi === pi ? 'on' : ''}`}
                        style={{ left: `${X(t)}%`, top: `${Y(f)}%`, borderColor: LINE_KINDS[l.kind].color }}
                        onPointerDown={(e) => startDotDrag(e, l.id, pi)} onDoubleClick={(e) => { e.stopPropagation(); removeDot(l.id, pi); setPick({ g: 'lines', id: l.id }); }} />)))}
                {show.marks && w.marks.map((m) => (
                  <span key={m.id} className={`cr-mark ${pick && pick.id === m.id ? 'on' : ''} ${m.origin}`} style={{ left: `${X(m.t)}%`, top: `${Y(m.f)}%` }}
                        onPointerDown={(e) => { if (tool !== 'select') return; e.stopPropagation(); setPick({ g: 'marks', id: m.id }); }} />))}
                {show.call && (
                  <div className={`hcr-callbox ${pick && pick.g === 'call' ? 'sel' : ''}`} style={{ left: `${X(w.call.t0)}%`, top: `${Y(w.call.f1)}%`,
                    width: `${X(w.call.t1) - X(w.call.t0)}%`, height: `${Y(w.call.f0) - Y(w.call.f1)}%` }}>
                    <button type="button" className="hcr-grip" aria-label="Whole call box: drag to move"
                            onPointerDown={(e) => startBoxDrag(e, 'call', 'move')}>Call</button>
                    {tool === 'select' && ['nw', 'ne', 'sw', 'se'].map((m) => (
                      <span key={m} className={`hcr-handle ${m}`} style={{ background: '#fff' }} onPointerDown={(e) => startBoxDrag(e, 'call', m)} />))}
                  </div>)}
                {show.boxes && w.boxes.map((b) => {
                  const on = pick && pick.g === 'boxes' && pick.id === b.id;
                  return (
                    <div key={b.id} role="button" tabIndex={0} aria-label={`${(section.boxes[b.kind] || {}).name || b.kind} box ${b.t0}–${b.t1} s`}
                         className={`hcr-box ${on ? 'sel' : ''} ${tool === 'select' ? '' : 'cr-passive'}`}
                         onPointerDown={(e) => startBoxDrag(e, b.id, 'move')}
                         style={{ left: `${X(b.t0)}%`, top: `${Y(b.f1)}%`, width: `${Math.max(0.6, X(b.t1) - X(b.t0))}%`,
                           height: `${Math.max(1, Y(b.f0) - Y(b.f1))}%`, borderColor: kindColor(b.kind) }}>
                      {on && tool === 'select' && ['nw', 'ne', 'sw', 'se'].map((m) => (
                        <span key={m} className={`hcr-handle ${m}`} style={{ background: kindColor(b.kind) }} onPointerDown={(e) => startBoxDrag(e, b.id, m)} />))}
                    </div>);
                })}
                {zoomBox && <div className="hcr-box cr-rubber cr-zoombox" style={{ left: `${X(zoomBox.t0)}%`, top: `${Y(zoomBox.f1)}%`,
                  width: `${X(zoomBox.t1) - X(zoomBox.t0)}%`, height: `${Y(zoomBox.f0) - Y(zoomBox.f1)}%` }} />}
                {rubber && <div className="hcr-box cr-rubber" style={{ left: `${X(rubber.t0)}%`, top: `${Y(rubber.f1)}%`, width: `${X(rubber.t1) - X(rubber.t0)}%`,
                  height: `${Y(rubber.f0) - Y(rubber.f1)}%`, borderColor: kindColor(boxKind) }} />}
                {show.parts && section.partsBar && w.parts.map((p) => (
                  <div key={p.id} className={`hcr-part ${pick && pick.id === p.id ? 'cr-part-on' : ''}`} title={PART[p.k]?.[1]}
                       style={{ left: `${X(p.a)}%`, width: `${X(p.b) - X(p.a)}%`, background: PART[p.k]?.[0] || '#6e7681' }}>{p.k}</div>))}
              </div>
            </div>
            <div className="hcr-xaxis hcr-mono">{ticks.map((t) => <span key={t}>{t} s</span>)}</div>

            {c.chorus && (
              <div className="hcr-note hcr-chorus-note"><strong>Chorus — not precise yet.</strong> {c.chorusNote || 'Several whales call at once here. Only the clearest calls are boxed and their limits are not precise yet.'} The
                engine boxed this call from its clearest lines only; parts of it, or another whale's call, may be outside or inside the box.</div>)}
            {c.aliasNote && (
              <div className="hcr-note"><strong>Possible aliasing in the recording.</strong> The recording cannot hold sound above {c.sampleRate ? c.sampleRate / 2000 : '?'} kHz;
                higher harmonics fold back into the picture and move opposite to the pitch. The blue dotted box marks the band.</div>)}

            {locked ? <p className="cr-saved">Saved — this call is done. Its review is stored with every line, box and label.</p>
              : <SelectedEditor {...{ section, sb, sl, sp, sm, pick, change, setPick, removeDot, extendLine, materialise, getBox, fitCall: () => change((s) => fitCallToLines(s, fmax)) }} />}
          </div>

          <div className="ct-card">
            <div className="hcr-row-between"><h3>Everything on this call{section.partsBar ? <> <Info id="parts" /></> : null}</h3>
              {removedN > 0 && <button type="button" className="ct-btn" onClick={() => change(restoreAll)}>Restore {removedN} deleted</button>}</div>
            <div className="cr-groups">
              <Group title="Whole call" k="call" show={show} setShow={setShow}>
                {row('call', 'call', 'Whole call', `${w.call.t0.toFixed(2)}–${w.call.t1.toFixed(2)} s · ${Math.round(w.call.f0)}–${Math.round(w.call.f1)} Hz`, w.call.origin, '#fff')}
              </Group>
              {section.tools.some((t) => LINE_KINDS[t]) && (
                <Group title="Pitch lines" k="lines" n={w.lines.length} show={show} setShow={setShow}
                       empty="No lines. Choose Draw F0 and click along the call.">
                  {w.lines.map((l, i) => row('lines', l.id, lineName(l, w.lines.filter((x) => x.kind === l.kind).indexOf(l)),
                    l.follows ? 'follows the F0 line' : `${l.points.length} dots · ${span(l.points)}`, l.origin, LINE_KINDS[l.kind].color,
                    <button type="button" className="cr-mini" onClick={() => extendLine(l.id)}>{l.follows ? 'Edit' : 'Extend'}</button>))}
                </Group>)}
              <Group title="Boxes" k="boxes" n={w.boxes.length} show={show} setShow={setShow} empty="No boxes. Choose Draw box and drag on the picture.">
                {w.boxes.map((b) => row('boxes', b.id, (section.boxes[b.kind] || {}).name || b.kind, `${b.t0}–${b.t1} s · ${b.f0}–${b.f1} Hz`, b.origin, kindColor(b.kind)))}
              </Group>
              {section.partsBar && (
                <Group title="Parts of the call (bar on top)" k="parts" n={w.parts.length} show={show} setShow={setShow}
                       action={<button type="button" className="cr-mini" onClick={() => { const id = uid('p'); change((s) => s.parts.push({ id, k: 'T', a: s.call.t0, b: r2((s.call.t0 + s.call.t1) / 2), origin: 'added' })); setPick({ g: 'parts', id }); }}>+ Add part</button>}>
                  {w.parts.map((p) => row('parts', p.id, `${p.k} — ${PART[p.k]?.[1] || ''}`, `${p.a}–${p.b} s`, p.origin, PART[p.k]?.[0]))}
                </Group>)}
              {(section.tools.includes('mark') || w.marks.length > 0) && (
                <Group title="Clicks" k="marks" n={w.marks.length} show={show} setShow={setShow} empty="No clicks marked. Choose Mark clicks and click on each one."
                       action={w.marks.length > 0 && <button type="button" className="cr-mini" onClick={() => { change((s) => { [...s.marks].forEach((m) => removeItem(s, 'marks', m.id)); }); setPick(null); }}>Delete all</button>}>
                  <li className="cr-el"><span className="ct-muted cr-el-detail">
                    {['engine', 'adjusted', 'added'].map((o) => `${w.marks.filter((m) => m.origin === o).length} ${ORIGIN[o]}`).join(' · ')} — select one on the picture and press Delete to remove it</span></li>
                </Group>)}
            </div>
          </div>
        </div>

        <aside className="hcr-side">
          <TypeCard section={section} entry={c} label={w.label} setLabel={change} moveTo={moveTo} call={w.call} library={library} />

          <div className="ct-card">
            {full && <>
            <div className="hcr-row-between"><h3>Measurements <Info id="measurements" /></h3>
              <span className="ct-muted">{Object.keys(drv).length ? 'blue = from your drawing' : ''}</span></div>
            <div className="hcr-mtable">
              {groups.map(([g, keys]) => (
                <div key={g}>
                  {groups.length > 1 && <div className="hcr-group">{g}</div>}
                  {keys.map((k) => {
                    const [label, unit] = LABELS[k] || [k, ''];
                    const eng = fmt(c.m?.[k]); const v = shownVal(k);
                    const fromDraw = !(k in w.edits) && k in drv && String(drv[k]) !== eng;
                    return (
                      <div key={k} className="hcr-mrow">
                        <label htmlFor={`m-${k}`}>{label} <span className="ct-muted">{unit}</span></label>
                        <span className="hcr-mono ct-muted hcr-eng">{eng}</span>
                        <input id={`m-${k}`} className={`hcr-input hcr-mono ${k in w.edits && w.edits[k] !== eng ? 'changed' : ''} ${fromDraw ? 'cr-drawn' : ''}`}
                               value={v} onChange={(e) => change((s) => { s.edits[k] = e.target.value; }, false)} />
                      </div>);
                  })}
                </div>))}
            </div>
            </>}

            {(() => {          // partial call (13 Oct 2026): saved with the review for engine training
              const ex = c.call && w.call.origin !== 'engine' ? callExtent(c.call, w.call, c) : null;
              if (!ex || !ex.partial) return null;
              const bits = [ex.startEarlier_s > 0.0005 && `starts ${ex.startEarlier_s.toFixed(2)} s earlier`, ex.endLater_s > 0.0005 && `ends ${ex.endLater_s.toFixed(2)} s later`,
                ex.lowerHz > 0 && ex.partialFreq && `${Math.round(ex.lowerHz)} Hz lower`, ex.higherHz > 0 && ex.partialFreq && `${Math.round(ex.higherHz)} Hz higher`].filter(Boolean);
              return (<p className="cr-warn hcr-small cr-partial" title="Saved with your review: the engine learns that it boxed only part of this call">
                Partial call: the engine’s box covered {Math.round(ex.covered * 100)} % of the call ({bits.join(', ')}). This is saved for engine training{ex.beyondPicture ? '; the call reaches past the engine’s picture, so training pictures will be drawn again from the sound' : ''}.</p>);
            })()}
            <DecisionBar saving={st === 'saving'} saved={locked} confirmText={moving ? `${confirmText} & move` : confirmText} note={note} error={error}
                         onReset={() => { hist.current[c.id] = []; setWork((p) => { const n = { ...p }; delete n[c.id]; return n; }); setPick(null); }}
                         onSubmit={submit} />
          </div>
          {/* the library examples last: the decision (Confirm / Dismiss) stays near the top (13 Oct 2026) */}
          {/* admins, researchers and decoders save calls: private, community or both (13 Oct 2026) */}
          {library && LIBRARY_SECTIONS.includes(section.key) && (
            <LibraryCard library={library} section={section} entry={c} state={w} meta={{ ...meta, ...(c._meta || {}) }} locked={false} sig={sig}
                         review={() => toReviewDoc(c, w, section, { ...meta, ...(c._meta || {}) })} />)}
        </aside>
      </div>
    </div>
  );
}

function Group({ title, k, n, show, setShow, children, empty, action }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <section className="cr-group">
      <div className="cr-group-head">
        <label className="ct-check"><input type="checkbox" checked={!!show[k]} onChange={() => setShow({ ...show, [k]: !show[k] })} />
          <strong>{title}</strong>{n !== undefined && <span className="ct-muted"> ({n})</span>}</label>
        {action}
      </div>
      {items.length ? <ul className="cr-ellist">{items}</ul> : <p className="ct-muted hcr-small">{empty}</p>}
    </section>);
}

function SelectedEditor({ section, sb, sl, sp, sm, pick, change, setPick, removeDot, extendLine, materialise, getBox, fitCall }) {
  if (!pick) return <p className="ct-muted cr-sel-none">Nothing selected. Click an item on the picture or in the list below.</p>;
  if (sb) {
    const isCall = pick.g === 'call';
    return (
      <div className="hcr-boxedit cr-sel">
        {isCall ? <span className="hcr-boxname">Whole call</span> : (
          <label>Kind<select className="hcr-input" value={sb.kind} onChange={(e) => change((s) => { const b = getBox(s, pick.id); b.kind = e.target.value; markAdjusted(b); })}>
            {Object.entries(section.boxes).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
            {!section.boxes[sb.kind] && <option value={sb.kind}>{sb.kind}</option>}</select></label>)}
        {[['t0', 'Start (s)', 0.01], ['t1', 'End (s)', 0.01], ['f0', 'Low (Hz)', 10], ['f1', 'High (Hz)', 10]].map(([k, l, stp]) => (
          <label key={k}>{l}<input className="hcr-input" type="number" step={stp} value={sb[k]}
            onChange={(e) => change((s) => { const b = getBox(s, pick.id); b[k] = Number(e.target.value); markAdjusted(b); })} /></label>))}
        <span className="ct-muted">{ORIGIN[sb.origin]}</span>
        {isCall && <button type="button" className="ct-btn" onClick={fitCall}>Fit to the lines</button>}
      </div>);
  }
  if (sl) {
    return (
      <div className="hcr-boxedit cr-sel">
        <label>Line<select className="hcr-input" value={sl.kind} onChange={(e) => change((s) => { const l = s.lines.find((x) => x.id === sl.id); l.kind = e.target.value; if (l.kind === 'f0') l.k = 1; markAdjusted(l); })}>
          {Object.entries(LINE_KINDS).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}</select></label>
        {sl.kind !== 'f0' && (
          <label>{sl.kind === 'harmonic' ? 'Harmonic number (×F0)' : 'Below F0 (F0 ÷)'}<input className="hcr-input" type="number" min={2} step={1}
            value={sl.kind === 'harmonic' ? sl.k : Math.round(1 / (sl.k || 0.5))}
            onChange={(e) => change((s) => { const l = s.lines.find((x) => x.id === sl.id); const v = Math.max(2, Number(e.target.value) || 2); l.k = l.kind === 'harmonic' ? v : 1 / v; markAdjusted(l); })} /></label>)}
        {sl.follows ? (
          <><span className="ct-muted">Drawn as {sl.k} × the F0 line.</span>
            <button type="button" className="ct-btn" onClick={() => materialise(sl.id)}>Edit its dots</button></>
        ) : (
          <><span className="ct-muted">{sl.points.length} dots · {ORIGIN[sl.origin]}{pick.pi != null && sl.points[pick.pi] ? ` · dot ${pick.pi + 1}: ${sl.points[pick.pi][0]} s, ${sl.points[pick.pi][1]} Hz` : ''}</span>
            <button type="button" className="ct-btn" onClick={() => extendLine(sl.id)}>Extend / add dots</button>
            {pick.pi != null && <button type="button" className="ct-btn" onClick={() => { removeDot(sl.id, pick.pi); setPick({ g: 'lines', id: sl.id }); }}>Delete dot</button>}</>)}
      </div>);
  }
  if (sp) {
    return (
      <div className="hcr-boxedit cr-sel">
        <label>Part<select className="hcr-input" value={sp.k} onChange={(e) => change((s) => { const p = s.parts.find((x) => x.id === sp.id); p.k = e.target.value; markAdjusted(p); })}>
          {Object.entries(PART).map(([k, v]) => <option key={k} value={k}>{k} — {v[1]}</option>)}</select></label>
        {[['a', 'Start (s)'], ['b', 'End (s)']].map(([k, l]) => (
          <label key={k}>{l}<input className="hcr-input" type="number" step={0.01} value={sp[k]}
            onChange={(e) => change((s) => { const p = s.parts.find((x) => x.id === sp.id); p[k] = Number(e.target.value); markAdjusted(p); })} /></label>))}
      </div>);
  }
  if (sm) {
    return (
      <div className="hcr-boxedit cr-sel">
        <span className="hcr-boxname">Click mark</span>
        <span className="ct-muted">{sm.t} s · {sm.f} Hz · {ORIGIN[sm.origin]} — press Delete to remove it</span>
      </div>);
  }
  return null;
}
